import type { HttpEvent, HttpHandlerFn, HttpRequest } from "@angular/common/http";
import { inject } from "@angular/core";
import type { Observable } from "rxjs";
import { BehaviorSubject, defer, filter, finalize, pairwise, startWith, switchMap, takeUntil, timer } from "rxjs";
import { ApplicationInsightsService } from "../services/application-insights.service";
import { UiBlockService } from "../services/ui-block.service";
import { excludeFromSpinner } from "./spinner-excluded-operations";

let count = 0;
const activeCount$ = new BehaviorSubject<number>(0);
let spinnerVisible = false;
let setupDone = false;

function ensureSpinnerSetup(blocker: UiBlockService, appInsights: ApplicationInsightsService): void {
  if (setupDone || !blocker || !appInsights) return;
  setupDone = true;

  activeCount$
    .pipe(
      startWith(0),
      pairwise(),
      filter(([prev, curr]) => prev === 0 && curr > 0),
      switchMap(() => timer(950).pipe(takeUntil(activeCount$.pipe(filter((c) => c === 0)))))
    )
    .subscribe(() => {
      if (!spinnerVisible) {
        spinnerVisible = true;
        blocker.show();
        appInsights.startTrackEvent("Spinner");
      }
    });

  activeCount$
    .pipe(
      filter((c) => c === 0),
      switchMap(() => timer(50).pipe(takeUntil(activeCount$.pipe(filter((c) => c > 0)))))
    )
    .subscribe(() => {
      if (spinnerVisible) {
        spinnerVisible = false;
        blocker.hide();
        appInsights.stopTrackEvent("Spinner");
      }
    });
}

export function uiBlockInterceptor(req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
  const blocker = inject(UiBlockService);
  const appInsights = inject(ApplicationInsightsService);

  ensureSpinnerSetup(blocker, appInsights);

  const url = req.url ?? "";
  const isGraphQL = url.endsWith("api/graphql");
  const body = req.body;
  const allExcluded = Array.isArray(body)
    ? body.every((op) => excludeFromSpinner.includes(op?.operationName))
    : excludeFromSpinner.includes(body?.operationName);
  if (!isGraphQL || allExcluded) {
    return next(req);
  } else {
    return defer(() => {
      count++;
      activeCount$.next(count);
      return next(req).pipe(
        finalize(() => {
          count = Math.max(0, count - 1);
          activeCount$.next(count);
        })
      );
    });
  }
}
